import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Grid,
  Box,
  TextField,
  FormControl,
  MenuItem,
} from "@mui/material";
import { Breadcrumb, SimpleCard } from "app/components";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  UPDATE_HOLIDAY,
  GET_BY_ID_HOLIDAY,
} from "app/redux/actions/holidays";
import { useAuth } from "app/hooks/useAuth";
import { toast } from "react-toastify";
import Loading from "app/components/MatxLoading";

const EditHoliday = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  let auth = useAuth();

  const { getById, getBYIdLoading, editLoading } = useSelector(
    (state) => state.holidays
  );

  // ✅ Fetch holiday details on load
  useEffect(() => {
    if (id) {
      dispatch(GET_BY_ID_HOLIDAY(id, auth.token));
    }
  }, [id, dispatch, auth.token]);

  // ✅ Validation schema
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      title: Yup.string().required("Title is required."),
      description: Yup.string().required("Description is required."),
      startDate: Yup.date().required("Start Date is required."),
      endDate: Yup.date()
        .required("End Date is required.")
        .min(Yup.ref("startDate"), "End Date cannot be earlier than Start Date"),
      isPublicHoliday: Yup.boolean().required(),
    });
  }, []);

  // ✅ Submit handler
  const handleSubmit = (values) => {
    const payload = {
      id,
      title: values.title,
      description: values.description,
      startDate: values.startDate,
      endDate: values.endDate,
      isPublicHoliday: values.isPublicHoliday,
    };

    dispatch(
      UPDATE_HOLIDAY(id, payload, auth.token, moveToNext, moveToFailure)
    );
  };

  const moveToNext = () => {
    toast.success("Holiday Updated Successfully");
    navigate(-1);
  };

  const moveToFailure = (message) => {
    toast.error(message);
  };

  if (getBYIdLoading) return <Loading />;

  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Edit Holiday" }]} />
      </Box>
      <Box padding={2}>
        <Formik
          enableReinitialize
          initialValues={{
            title: getById?.title || "",
            description: getById?.description || "",
            startDate: getById?.startDate
              ? new Date(getById.startDate)
              : null,
            endDate: getById?.endDate ? new Date(getById.endDate) : null,
            isPublicHoliday:
              getById?.isPublicHoliday !== undefined
                ? getById.isPublicHoliday
                : true,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
            submitCount,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Title */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Title *"
                    name="title"
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={
                      (touched.title || submitCount > 0) &&
                      Boolean(errors.title)
                    }
                    helperText={
                      (touched.title || submitCount > 0) && errors.title
                    }
                  />
                </Grid>

                {/* Description */}


                {/* Start Date */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Start Date"
                        value={values.startDate}
                        onChange={(date) =>
                          setFieldValue("startDate", date)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              (touched.startDate || submitCount > 0) &&
                              Boolean(errors.startDate),
                            helperText:
                              (touched.startDate || submitCount > 0) &&
                              errors.startDate,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>

                {/* End Date */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="End Date"
                        value={values.endDate}
                        minDate={values.startDate}
                        onChange={(date) =>
                          setFieldValue("endDate", date)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              (touched.endDate || submitCount > 0) &&
                              Boolean(errors.endDate),
                            helperText:
                              (touched.endDate || submitCount > 0) &&
                              errors.endDate,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>

                {/* Public Holiday */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Public Holiday *"
                      name="isPublicHoliday"
                      value={values.isPublicHoliday}
                      onChange={handleChange}
                      error={
                        (touched.isPublicHoliday || submitCount > 0) &&
                        Boolean(errors.isPublicHoliday)
                      }
                      helperText={
                        (touched.isPublicHoliday || submitCount > 0) &&
                        errors.isPublicHoliday
                      }
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} >
                  <TextField
                    label="Description *"
                    name="description"
                    fullWidth
                    multiline
                    rows={4}
                    value={values.description}
                    onChange={handleChange}
                    error={(touched.description || submitCount > 0) && Boolean(errors.description)}
                    helperText={(touched.description || submitCount > 0) && errors.description}
                  />

                </Grid>
                {/* Submit */}
                {editLoading ? (
                  <Loading />
                ) : (
                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Update Holiday
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          )}
        </Formik>
      </Box>
    </SimpleCard>
  );
};

export default EditHoliday;
